/* ==========================================================================
   The Slipper Spot — storefront behaviour.

   Dependency-free and small: nothing here loads a framework, and every page
   still works as a server-rendered page if this file fails.
   ========================================================================== */
(function () {
  'use strict';

  var AR_DIGITS = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
  var isArabic = document.documentElement.lang === 'ar';
  var reduceMotion = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  function digits(value) {
    var s = String(value);
    if (!isArabic) return s;
    return s.replace(/[0-9]/g, function (d) {
      return AR_DIGITS[+d];
    });
  }

  function fill(template, n) {
    return String(template || '').replace('[n]', digits(n));
  }

  /* --------------------------------------------------------- announcing */

  // Persistent live regions (rendered in the layout) so screen readers hear
  // results reliably: a region that appears or fills in the same moment is
  // often skipped on TalkBack/Chrome.
  function announce(message, assertive) {
    var region = document.querySelector(assertive ? '[data-ssr-alert]' : '[data-ssr-announce]');
    if (!region || !message) return;
    region.textContent = '';
    setTimeout(function () {
      region.textContent = message;
    }, 60);
  }

  /* ---------------------------------------------------------------- toast */

  var toastTimer = null;

  // One message at a time, optionally with a link ("View cart"). Announced
  // politely; stays long enough to read and tap.
  function toast(message, action) {
    if (!message) return;
    var rail = document.querySelector('.ssr-toastrail');
    if (!rail) {
      rail = document.createElement('div');
      rail.className = 'ssr-toastrail';
      rail.setAttribute('role', 'status');
      rail.setAttribute('aria-live', 'polite');
      document.body.appendChild(rail);
    }
    rail.innerHTML = '';
    rail.removeAttribute('role');
    rail.removeAttribute('aria-live');
    var pill = document.createElement('div');
    pill.className = 'ssr-toast';
    var text = document.createElement('span');
    text.textContent = message;
    pill.appendChild(text);
    if (action && action.href) {
      var link = document.createElement('a');
      link.className = 'ssr-toast__action';
      link.href = action.href;
      link.textContent = action.label;
      pill.appendChild(link);
    }
    rail.appendChild(pill);
    announce(message, true);
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () {
      rail.innerHTML = '';
    }, action ? 5000 : 2600);
  }

  /* ----------------------------------------------------------- cart count */

  function setCartCount(count) {
    var el = document.querySelector('[data-ssr-cart-count]');
    if (!el) return;
    el.textContent = digits(count);
    el.classList.remove('is-popping');
    void el.offsetWidth;
    el.classList.add('is-popping');
  }

  function refreshCartCount() {
    return fetch(ROOT + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        setCartCount(cart.item_count);
        return cart;
      });
  }

  /* A scratch-card code that was won but doesn't apply to this cart (the code
     was changed in Discounts, say): checkout would drop it without a word, so
     say it here. Nothing to flag when another offer already beat it. */
  function rewardCodeProblem(cart) {
    var code = null;
    try {
      code = localStorage.getItem('ssrRewardCode');
    } catch (e) {}
    if (!code || !cart || !cart.item_count) return null;
    var entry = (cart.discount_codes || []).filter(function (x) {
      return String(x.code).toLowerCase() === code.toLowerCase();
    })[0];
    if (!entry || entry.applicable) return null;
    if ((cart.total_discount || 0) > 0) return null;
    return code;
  }

  function initCodeWarn() {
    var el = document.querySelector('[data-ssr-codewarn]');
    if (!el) return;
    fetch(ROOT + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        var code = rewardCodeProblem(cart);
        if (!code) return;
        el.textContent = String(el.getAttribute('data-t') || '').replace('[code]', code);
        var wa = document.querySelector('.ssr-summary .ssr-wa');
        if (wa) {
          var a = document.createElement('a');
          a.href = wa.href;
          a.target = '_blank';
          a.rel = 'noopener';
          a.textContent = el.getAttribute('data-cta') || '';
          el.appendChild(a);
        }
        el.hidden = false;
      })
      .catch(function () {});
  }

  /* ------------------------------------------------- collection controls */

  // Filters that live in the URL and run in the browser, because Liquid cannot
  // read arbitrary query parameters: ?instock=1 hides sold-out styles, and
  // ?size=42 keeps styles with EU 42 in stock (every card carries its in-stock
  // EU sizes in data-eu). Both survive share and back.
  function initCollection() {
    var root = document.querySelector('[data-ssr-collection]');
    if (!root) return;

    var params = new URLSearchParams(location.search);
    var on = params.get('instock') === '1';
    var size = (params.get('size') || '').replace(/[^0-9]/g, '');
    root.setAttribute('data-ssr-instock-only', on ? 'true' : 'false');

    // Every control rewrites only its own parameter and keeps the others, so
    // "In stock" + "EU 42" + a sort survive each other.
    function urlWith(changes) {
      var u = new URL(location.href);
      u.searchParams.delete('page');
      Object.keys(changes).forEach(function (k) {
        if (changes[k] === null) u.searchParams.delete(k);
        else u.searchParams.set(k, changes[k]);
      });
      return u.pathname + u.search;
    }

    var toggle = root.querySelector('[data-ssr-instock]');
    if (toggle) {
      toggle.classList.toggle('is-on', on);
      // A link cannot carry aria-pressed; say the state and what it does.
      if (on) toggle.setAttribute('aria-label', toggle.getAttribute('data-label-on') || '');
      else toggle.removeAttribute('aria-label');
      toggle.setAttribute('href', urlWith({ instock: on ? null : '1' }));
    }

    var count = root.querySelector('[data-ssr-count]');
    if (count && !size) {
      count.textContent = on ? count.getAttribute('data-count-instock') : count.getAttribute('data-count-all');
    }

    var sortLink = root.querySelector('[data-ssr-sortlink]');
    if (sortLink) {
      var sortTo = new URL(sortLink.getAttribute('href'), location.href).searchParams.get('sort_by');
      sortLink.setAttribute('href', urlWith({ sort_by: sortTo || null }));
    }
    root.querySelectorAll('.ssr-sizepick a.ssr-footbed').forEach(function (a) {
      var n = new URL(a.getAttribute('href'), location.href).searchParams.get('size');
      if (n) a.setAttribute('href', urlWith({ size: n }));
    });

    if (!size) return;
    var shown = 0;
    var visible = [];
    root.querySelectorAll('.ssr-card[data-eu]').forEach(function (card) {
      var keep = card.getAttribute('data-eu').indexOf(' ' + size + ' ') !== -1;
      var cell = card.parentElement && card.parentElement.matches('li, [role="listitem"]') ? card.parentElement : card;
      cell.hidden = !keep;
      if (keep) {
        shown++;
        visible.push(cell);
      }
    });
    // The cash-on-delivery strip sits after the fourth card; move it with
    // the filter so it never leaves a lone card and an empty cell above it.
    var strip = root.querySelector('.ssr-grid__note');
    if (strip) {
      if (visible.length >= 4) visible[3].insertAdjacentElement('afterend', strip);
      else strip.hidden = true;
    }
    var pill = root.querySelector('[data-ssr-sizepill]');
    if (pill) {
      pill.hidden = false;
      var label = pill.querySelector('[data-ssr-sizepill-label]');
      if (label) label.textContent = String(pill.getAttribute('data-label') || '').replace('[n]', size);
      pill.setAttribute('aria-label', String(pill.getAttribute('data-clear-label') || '').replace('[n]', size));
      pill.setAttribute('href', urlWith({ size: null }));
    }
    if (count) count.textContent = fill(count.getAttribute('data-count-size'), shown);
    var empty = root.querySelector('[data-ssr-size-empty]');
    if (empty) empty.hidden = shown > 0;
  }

  /* ------------------------------------------------------- size finder */

  function initSizeGuide() {
    var sheet = document.querySelector('[data-ssr-sizeguide]');
    if (!sheet) return null;

    var lastTrigger = null;
    var listeners = [];

    function open(trigger) {
      lastTrigger = trigger || null;
      if (typeof sheet.showModal === 'function') sheet.showModal();
      else sheet.setAttribute('open', '');
      listeners.forEach(function (fn) {
        fn(true);
      });
    }

    function close() {
      if (typeof sheet.close === 'function') sheet.close();
      else sheet.removeAttribute('open');
      if (lastTrigger) lastTrigger.focus();
    }

    sheet.addEventListener('close', function () {
      listeners.forEach(function (fn) {
        fn(false);
      });
    });

    document.querySelectorAll('[data-ssr-sizeguide-open]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        open(btn);
      });
    });

    sheet.querySelectorAll('[data-ssr-sizeguide-close]').forEach(function (btn) {
      btn.addEventListener('click', close);
    });

    // The dialog fills the viewport, so a click that lands on it rather than
    // on the panel is a click on the scrim.
    sheet.addEventListener('click', function (event) {
      if (event.target === sheet) close();
    });

    return {
      el: sheet,
      open: open,
      close: close,
      onToggle: function (fn) {
        listeners.push(fn);
      },
    };
  }

  // Continental sizing: one Paris point is 2/3 cm, and a shoe carries about
  // 1.5 cm of toe allowance over the foot. Holds from kids' 19 to adults' 45.
  function euFromFootLength(cm) {
    return Math.round((cm + 1.5) * 1.5);
  }

  function initSizeFinder(sheet, ctx) {
    if (!sheet) return;

    var input = sheet.el.querySelector('[data-ssr-finder-input]');
    if (!input) return;

    var cmOut = sheet.el.querySelector('[data-ssr-finder-cm]');
    var reason = sheet.el.querySelector('[data-ssr-finder-reason]');
    var badge = sheet.el.querySelector('[data-ssr-finder-badge]');
    var result = sheet.el.querySelector('[data-ssr-finder-result]');
    var note = sheet.el.querySelector('[data-ssr-finder-note]');
    var apply = sheet.el.querySelector('[data-ssr-finder-apply]');
    var d = sheet.el.dataset;
    var t = {
      cm: d.tCm,
      based: d.tBased,
      use: d.tUse,
      close: d.tClose,
      note: d.tNote,
      between: d.tBetween,
      soldout: d.tSoldout,
      gone: d.tGone,
      none: d.tNone,
    };
    // A recommendation more than one EU size from the foot is not a fit.
    var FIT = 1;

    var STORE_KEY = 'ssr:foot';
    var picked = null;

    var hasSaved = false;
    try {
      var saved = parseFloat(localStorage.getItem(STORE_KEY));
      if (saved >= +input.min && saved <= +input.max) {
        input.value = saved;
        hasSaved = true;
      }
    } catch (e) {
      /* private mode — start from the default */
    }

    // With no saved foot, open on the foot length for this product's middle
    // size, so a kids' style never opens recommending against an adult foot.
    if (!hasSaved) {
      // Middle of the sizes in stock (all sizes if none are).
      var all = ctx.sizes().filter(function (s) {
        return s.eu;
      });
      var stocked = all.filter(function (s) {
        return s.qty > 0;
      });
      var eus = (stocked.length ? stocked : all)
        .map(function (s) {
          return s.eu;
        })
        .sort(function (a, b) {
          return a - b;
        });
      if (eus.length) {
        var mid = eus[Math.floor((eus.length - 1) / 2)];
        var cm0 = Math.round((mid / 1.5 - 1.5) * 2) / 2;
        input.value = Math.max(+input.min, Math.min(+input.max, cm0));
      }
    }

    // Size labels go in as isolated, unbreakable runs so "C11 — EU 28-29"
    // never splits across lines or reorders inside Arabic.
    function noteWith(template, map) {
      if (!note) return;
      note.textContent = '';
      String(template || '')
        .split(/(\[n\]|\[a\]|\[b\])/)
        .forEach(function (part) {
          if (!part) return;
          if (Object.prototype.hasOwnProperty.call(map, part)) {
            var b = document.createElement('bdi');
            b.className = 'ssr-nowrap';
            b.textContent = map[part];
            note.appendChild(b);
          } else {
            note.appendChild(document.createTextNode(part));
          }
        });
    }

    function nearest(sizes, target, availableOnly) {
      var best = null;
      sizes.forEach(function (s) {
        if (!s.eu) return;
        if (availableOnly && s.qty <= 0) return;
        var dist = Math.abs(s.eu - target);
        if (!best || dist < best.d) best = { size: s, d: dist };
      });
      return best;
    }

    function render() {
      var cm = parseFloat(input.value);
      var target = euFromFootLength(cm);

      if (cmOut) cmOut.textContent = digits(cm);
      input.setAttribute('aria-valuetext', cm + ' ' + t.cm);
      if (reason) reason.textContent = String(t.based || '').replace('[n]', digits(cm));

      var sizes = ctx.sizes();
      var match = nearest(sizes, target, false);
      if (match && match.d > FIT) match = null;
      picked = match ? match.size : null;

      function none(text) {
        if (apply) {
          apply.disabled = true;
          apply.textContent = t.close;
        }
        if (typeof text === 'function') text();
        else if (note) note.textContent = text;
      }

      if (!picked) {
        if (badge) badge.textContent = '—';
        if (result) result.classList.remove('is-out');
        none(t.none);
        return;
      }

      if (picked.qty <= 0) {
        var gone = picked.label;
        var alt = nearest(sizes, target, true);
        if (alt && alt.d <= FIT) {
          // Lead with the size they can have; the sold-out one is the note.
          picked = alt.size;
          if (badge) badge.textContent = picked.label;
          if (result) result.classList.remove('is-out');
          if (apply) {
            apply.disabled = false;
            apply.textContent = String(t.use || '').replace('[n]', picked.label);
          }
          noteWith(t.soldout, { '[n]': gone, '[a]': picked.label });
        } else {
          if (badge) badge.textContent = gone;
          if (result) result.classList.add('is-out');
          picked = null;
          none(function () {
            noteWith(t.gone, { '[n]': gone });
          });
        }
        return;
      }

      if (badge) badge.textContent = picked.label;
      if (result) result.classList.remove('is-out');
      if (apply) {
        apply.disabled = false;
        apply.textContent = String(t.use || '').replace('[n]', picked.label);
      }

      // Flag a genuine straddle: the measurement sits between two sizes that
      // are equally close. The store's own advice is to take the larger one.
      var neighbour = null;
      sizes.forEach(function (s) {
        if (!s.eu || s.label === picked.label || s.qty <= 0) return;
        var dist = Math.abs(s.eu - target);
        if (Math.abs(dist - match.d) < 0.5 && s.eu > picked.eu) neighbour = s;
      });

      if (neighbour) noteWith(t.between, { '[a]': picked.label, '[b]': neighbour.label });
      else if (note) note.textContent = t.note;
    }

    input.addEventListener('input', render);
    input.addEventListener('change', function () {
      try {
        localStorage.setItem(STORE_KEY, input.value);
      } catch (e) {
        /* nothing to persist to */
      }
    });

    if (apply) {
      apply.addEventListener('click', function () {
        if (picked) ctx.select(picked.label);
        sheet.close();
      });
    }

    // Availability changes with the selected colour, so recompute on open.
    sheet.onToggle(function (isOpen) {
      if (isOpen) render();
    });

    render();
  }

  /* ------------------------------------------------------------ product */

  function initProduct(sheet) {
    var root = document.querySelector('[data-ssr-product]');
    if (!root) return;

    var dataEl = root.querySelector('[data-ssr-variants]');
    if (!dataEl) return;

    var data;
    var alternatives = [];
    try {
      data = JSON.parse(dataEl.textContent);
      var altEl = root.querySelector('[data-ssr-alternatives]');
      if (altEl) alternatives = JSON.parse(altEl.textContent) || [];
    } catch (e) {
      return;
    }

    var d = root.dataset;
    var t = {
      soldout: d.tSoldout,
      notInSize: d.tNotinsize,
      add: d.tAdd,
      choose: d.tChoose,
      chooseShort: d.tChooseShort,
      pickFirst: d.tPickFirst,
      from: d.tFrom,
      other: d.tOther,
      left: d.tLeft,
      sizeAvailable: d.tSizeavailable,
      added: d.tAdded,
      viewCart: d.tViewCart,
      error: d.tError,
      limit: d.tLimit,
      waSize: d.tWaSize,
      waBack: d.tWaBack,
      adding: d.tAdding,
    };
    var moneyPrefix = d.moneyPrefix || '';
    var moneySuffix = d.moneySuffix || '';

    function money(cents) {
      var amount = Math.round(cents / 100);
      var text = amount >= 1000 ? amount.toLocaleString('en-US') : String(amount);
      return moneyPrefix + digits(text) + moneySuffix;
    }

    var state = { size: data.initSize || '', colour: data.initColour || '', qty: 1 };

    var sizeButtons = Array.prototype.slice.call(root.querySelectorAll('[data-ssr-size]'));
    var swatches = Array.prototype.slice.call(root.querySelectorAll('[data-ssr-colour]'));
    var recovery = root.querySelector('[data-ssr-recovery]');
    var sizeLeft = root.querySelector('[data-ssr-size-left]');
    var addBtn = root.querySelector('[data-ssr-add]');
    var buyNow = root.querySelector('[data-ssr-buynow]');
    var variantInput = root.querySelector('[data-ssr-variant-id]');
    var qtyInput = root.querySelector('[data-ssr-qty-input]');
    var qtyDisplay = root.querySelector('[data-ssr-qty-display]');
    var qtyMinus = root.querySelector('[data-ssr-qty="-1"]');
    var qtyPlus = root.querySelector('[data-ssr-qty="1"]');
    var priceEl = root.querySelector('[data-ssr-price]');
    var fromEl = root.querySelector('[data-ssr-from]');
    var compareEl = root.querySelector('[data-ssr-compare]');
    var colourName = root.querySelector('[data-ssr-colour-name]');
    var sizeName = root.querySelector('[data-ssr-size-name]');
    var sizeHint = root.querySelector('[data-ssr-size-hint]');
    var colourStep = root.querySelector('[data-ssr-opt="colour"]');
    var sizeStep = root.querySelector('[data-ssr-opt="size"]');
    var picked = root.querySelector('[data-ssr-picked]');
    var pickedThumb = root.querySelector('[data-ssr-picked-thumb]');
    var pickedValue = root.querySelector('[data-ssr-picked-value]');
    var waSize = root.querySelector('[data-ssr-wa-size]');
    var sticky = document.querySelector('[data-ssr-sticky]');
    var stickyMeta = document.querySelector('[data-ssr-sticky-meta]');
    var stickyPick = document.querySelector('[data-ssr-sticky-pick]');
    var stickyThumb = document.querySelector('[data-ssr-sticky-thumb]');
    var stickyAdd = document.querySelector('[data-ssr-sticky-add]');
    var lastColour = state.colour;
    var lastSize = state.size;
    var busy = false;
    var justAdded = false;
    // Anything in the cart (from this page or earlier): "Buy now" becomes
    // "Go to checkout" and takes the shopper straight there.
    var cartItems = parseInt(d.cartCount, 10) || 0;
    // Variants this visit has put in the cart: for these, the sticky bar and
    // "buy now" go straight to checkout instead of adding a second pair.
    var addedIds = {};
    var cartCache = null;
    var added = root.querySelector('[data-ssr-added]');
    var saveEl = root.querySelector('[data-ssr-save]');
    var sizesGroup = root.querySelector('[data-ssr-sizes]');

    // Buttons hold their words in a label span next to the trace SVG, so a
    // label change never removes the SVG.
    function setLabel(btn, text) {
      if (!btn) return;
      var label = btn.querySelector('[data-ssr-label]');
      if (label) label.textContent = text;
      else btn.textContent = text;
    }

    // The signature: when a pair lands in the cart, one white line traces the
    // inside of the navy capsule — the button briefly becomes the monogram.
    // Geometry is measured from the button, so it fits any width.
    function trace(btn) {
      if (!btn || reduceMotion) return;
      var svg = btn.querySelector('.ssr-trace');
      var rect = svg && svg.querySelector('rect');
      if (!rect) return;
      var w = btn.clientWidth;
      var h = btn.clientHeight;
      if (!w || !h) return;
      var inset = 4;
      var rw = w - inset * 2;
      var rh = h - inset * 2;
      var r = rh / 2;
      svg.setAttribute('viewBox', '0 0 ' + w + ' ' + h);
      rect.setAttribute('x', inset);
      rect.setAttribute('y', inset);
      rect.setAttribute('width', rw);
      rect.setAttribute('height', rh);
      rect.setAttribute('rx', r);
      var length = 2 * (rw - 2 * r) + 2 * Math.PI * r;
      svg.style.setProperty('--ssr-trace-len', length.toFixed(1));
      btn.classList.remove('is-tracing');
      void btn.offsetWidth;
      btn.classList.add('is-tracing');
      setTimeout(function () {
        btn.classList.remove('is-tracing');
      }, 900);
    }

    // Replay a one-shot CSS animation by re-adding its class. The resting look
    // never depends on the animation running.
    function replay(el, cls) {
      if (!el || reduceMotion) return;
      el.classList.remove(cls);
      void el.offsetWidth;
      el.classList.add(cls);
    }

    function variantFor(size, colour) {
      for (var i = 0; i < data.variants.length; i++) {
        var v = data.variants[i];
        if ((!data.hasSize || v.size === size) && (!data.hasColour || v.colour === colour)) return v;
      }
      return null;
    }

    function qtyFor(size, colour) {
      var v = variantFor(size, colour);
      return v ? v.q : 0;
    }

    // Units left in a size across every colour — decides between "gone in
    // this colour" and "gone everywhere".
    function sizeTotal(size) {
      var total = 0;
      for (var i = 0; i < data.variants.length; i++) {
        if (data.variants[i].size === size) total += data.variants[i].q;
      }
      return total;
    }

    // Units left in a colour across every size.
    function colourTotal(colour) {
      var total = 0;
      for (var i = 0; i < data.variants.length; i++) {
        if (!data.hasColour || data.variants[i].colour === colour) total += data.variants[i].q;
      }
      return total;
    }

    // Price of the in-stock variants of a colour, for before a size is chosen.
    function colourPrices(colour) {
      var min = null;
      var max = null;
      var compare = 0;
      data.variants.forEach(function (v) {
        if (data.hasColour && v.colour !== colour) return;
        if (v.q <= 0) return;
        if (min === null || v.price < min) {
          min = v.price;
          compare = v.compare;
        }
        if (max === null || v.price > max) max = v.price;
      });
      return { min: min, max: max, compare: compare };
    }

    function euOf(size) {
      for (var i = 0; i < sizeButtons.length; i++) {
        if (sizeButtons[i].dataset.ssrSize === size) return parseInt(sizeButtons[i].dataset.eu, 10) || 0;
      }
      return 0;
    }

    function needsSize() {
      return data.hasSize && !state.size;
    }

    function swatchFor(colour) {
      return swatches.filter(function (b) {
        return b.dataset.ssrColour === colour;
      })[0];
    }

    function pickParts() {
      var parts = [];
      if (data.hasColour) {
        var sw = swatchFor(state.colour);
        parts.push(sw ? sw.dataset.name : state.colour);
      }
      if (data.hasSize && state.size) parts.push(state.size);
      return parts;
    }

    function waMessage(template) {
      var parts = pickParts();
      var pick = parts.length ? ' (' + parts.join(' · ') + ')' : '';
      return String(template || '')
        .replace('[title]', d.title || '')
        .replace('[pick]', pick)
        .replace('[url]', d.url || '');
    }

    function waHref(template) {
      return 'https://wa.me/' + (d.wa || '') + '?text=' + encodeURIComponent(waMessage(template));
    }

    /* -- gallery: one swipeable strip; a colour's photo is that colour -- */

    var track = root.querySelector('[data-ssr-track]');
    var slides = track ? Array.prototype.slice.call(track.querySelectorAll('[data-ssr-slide]')) : [];
    var counter = root.querySelector('[data-ssr-count]');
    var programmatic = false;
    var settleTimer = null;

    function isRtl(el) {
      return getComputedStyle(el).direction === 'rtl';
    }

    function slideStep() {
      if (slides.length > 1) return Math.abs(slides[1].offsetLeft - slides[0].offsetLeft) || track.clientWidth;
      return track.clientWidth || 1;
    }

    function currentSlide() {
      if (!track || !slides.length) return 0;
      var i = Math.round(Math.abs(track.scrollLeft) / slideStep());
      return Math.max(0, Math.min(slides.length - 1, i));
    }

    function setDots(i) {
      if (counter) counter.textContent = digits(i + 1);
    }

    function goTo(i) {
      if (!track || i < 0 || i >= slides.length) return;
      if (currentSlide() === i) return;
      programmatic = true;
      var x = i * slideStep() * (isRtl(track) ? -1 : 1);
      track.scrollTo({ left: x, behavior: reduceMotion ? 'auto' : 'smooth' });
      setDots(i);
      clearTimeout(settleTimer);
      settleTimer = setTimeout(function () {
        programmatic = false;
      }, 700);
    }

    function slideForColour(colour) {
      for (var i = 0; i < slides.length; i++) {
        if (slides[i].dataset.colour === colour) return i;
      }
      return -1;
    }

    function settle() {
      var i = currentSlide();
      setDots(i);
      if (programmatic) {
        programmatic = false;
        return;
      }
      var colour = slides[i] && slides[i].dataset.colour;
      if (data.hasColour && colour && colour !== state.colour) {
        state.colour = colour;
        state.qty = 1;
        render(true);
      }
    }

    root.querySelectorAll('[data-ssr-gal-step]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (!track || slides.length < 2) return;
        var i = (currentSlide() + parseInt(btn.getAttribute('data-ssr-gal-step'), 10) + slides.length) % slides.length;
        track.scrollTo({ left: i * slideStep() * (isRtl(track) ? -1 : 1), behavior: reduceMotion ? 'auto' : 'smooth' });
      });
    });

    if (track && slides.length > 1) {
      track.addEventListener(
        'scroll',
        function () {
          clearTimeout(settleTimer);
          settleTimer = setTimeout(settle, 140);
        },
        { passive: true }
      );
    }

    /* -- rendering -- */

    // Size chips speak the same language as the colour tiles: chosen, and one
    // state per chip with its own words.
    function renderSizes() {
      sizeButtons.forEach(function (btn) {
        var size = btn.dataset.ssrSize;
        var here = qtyFor(size, state.colour);
        var anywhere = sizeTotal(size);
        var selected = size === state.size;
        var sub = btn.querySelector('.ssr-size__sub');

        btn.setAttribute('aria-pressed', selected ? 'true' : 'false');
        btn.classList.toggle('is-out', here <= 0);
        btn.classList.toggle('is-gone', here <= 0 && anywhere <= 0);
        btn.classList.toggle('is-other', here <= 0 && anywhere > 0);
        btn.classList.toggle('is-low', here > 0 && here <= 3);

        if (!sub) return;
        sub.className = 'ssr-size__sub';
        if (here <= 0 && anywhere <= 0) {
          sub.textContent = t.soldout;
          sub.classList.add('ssr-size__sub--gone');
        } else if (here <= 0) {
          sub.textContent = t.other;
          sub.classList.add('ssr-size__sub--other');
        } else if (here <= 3) {
          sub.textContent = fill(t.left, here);
          sub.classList.add('ssr-size__sub--low');
        } else {
          sub.textContent = '';
        }
      });

      var changed = lastSize !== state.size;
      if (sizeName && sizeName.textContent !== state.size) {
        sizeName.textContent = state.size;
        if (changed && state.size) replay(sizeName, 'is-changing');
      }
      if (sizeHint && !needsSize()) sizeHint.hidden = true;
      lastSize = state.size;
    }

    // Each swatch carries one state with its own words: sold out in every
    // size, not in the chosen size, or low. Before a size is chosen, "here"
    // means anywhere in that colour.
    function renderSwatches() {
      swatches.forEach(function (btn) {
        var colour = btn.dataset.ssrColour;
        var gone = colourTotal(colour) <= 0;
        var here = state.size ? qtyFor(state.size, colour) : colourTotal(colour);
        var notHere = !gone && here <= 0;
        var low = !gone && state.size && here > 0 && here <= 3;
        var label = btn.querySelector('[data-ssr-swatch-state]');

        btn.setAttribute('aria-pressed', colour === state.colour ? 'true' : 'false');
        btn.classList.toggle('is-gone', gone);
        btn.classList.toggle('is-unavailable', notHere);
        btn.classList.toggle('is-low', !!low);

        if (label) {
          if (gone) label.textContent = t.soldout;
          else if (notHere) label.textContent = String(t.notInSize || '').replace('[n]', state.size);
          else if (low) label.textContent = fill(t.left, here);
          else label.textContent = '';
        }
      });

      var current = swatchFor(state.colour);
      var changed = lastColour !== state.colour;
      if (colourName && current && colourName.textContent !== current.dataset.name) {
        colourName.textContent = current.dataset.name;
        if (changed) replay(colourName, 'is-changing');
      }
      lastColour = state.colour;
    }

    // A step's badge becomes a tick once that choice can be bought.
    function renderSteps(here) {
      if (colourStep) {
        var colourOk = colourTotal(state.colour) > 0;
        colourStep.classList.toggle('is-done', colourOk);
        colourStep.classList.toggle('is-blocked', !colourOk);
      }
      if (sizeStep) {
        sizeStep.classList.toggle('is-done', !!state.size && here > 0);
        sizeStep.classList.toggle('is-blocked', !!state.size && here <= 0);
      }
    }

    function colourImage() {
      var v = null;
      for (var i = 0; i < data.variants.length; i++) {
        if (data.variants[i].colour === state.colour && data.variants[i].img) {
          v = data.variants[i];
          break;
        }
      }
      var sw = swatchFor(state.colour);
      return (v && v.img) || (sw && sw.dataset.img) || '';
    }

    function setThumb(box, src, width) {
      if (!box || !src) return;
      var img = box.querySelector('img');
      var url = src.replace(/([?&])width=\d+/, '$1width=' + width);
      if (!img) {
        img = document.createElement('img');
        img.alt = '';
        box.textContent = '';
        box.appendChild(img);
      }
      if (img.getAttribute('src') !== url) {
        img.removeAttribute('srcset');
        img.src = url;
      }
    }

    // "Your pick" reads back exactly what goes in the bag, right above the
    // button — and says plainly when the size is still missing.
    function renderPicked() {
      if (!picked) return;
      var parts = pickParts();
      var key = parts.join('|') + (needsSize() ? '|?' : '');
      if (picked.dataset.key === key) return;
      var first = !picked.dataset.key;
      picked.dataset.key = key;

      if (pickedValue) {
        pickedValue.textContent = '';
        parts.forEach(function (part, i) {
          if (i) pickedValue.appendChild(document.createTextNode(' · '));
          var bdi = document.createElement('bdi');
          bdi.textContent = part;
          pickedValue.appendChild(bdi);
        });
        if (needsSize()) {
          if (parts.length) pickedValue.appendChild(document.createTextNode(' · '));
          var missing = document.createElement('span');
          missing.className = 'ssr-picked__missing';
          missing.textContent = t.chooseShort;
          pickedValue.appendChild(missing);
        }
      }
      picked.classList.toggle('is-incomplete', needsSize());
      if (data.hasColour) setThumb(pickedThumb, colourImage(), 120);
      if (!first) replay(picked, 'is-changing');
    }

    function renderRecovery(here, anywhere) {
      if (!recovery) return;
      recovery.innerHTML = '';
      recovery.hidden = true;
      if (!state.size || here > 0) return;

      var panel = document.createElement('div');
      panel.className = 'ssr-recovery';

      // A — the size exists, just not in this colour.
      if (anywhere > 0) {
        var msg = document.createElement('div');
        msg.className = 'ssr-recovery__msg';
        msg.textContent = d.tRecoveryColour;
        panel.appendChild(msg);

        var row = document.createElement('div');
        row.className = 'ssr-recovery__colours';
        swatches.forEach(function (btn) {
          var colour = btn.dataset.ssrColour;
          var count = qtyFor(state.size, colour);
          if (count <= 0) return;
          var chip = document.createElement('button');
          chip.type = 'button';
          chip.className = 'ssr-recovery__colour';
          var dot = document.createElement('span');
          dot.className = 'ssr-recovery__dot';
          if (btn.dataset.img) {
            dot.style.backgroundImage = 'url(' + JSON.stringify(btn.dataset.img) + ')';
            dot.classList.add('ssr-recovery__dot--photo');
          } else {
            dot.style.background = btn.dataset.hex;
          }
          var label = document.createElement('span');
          label.textContent = count <= 6 ? btn.dataset.name + ' · ' + fill(t.left, count) : btn.dataset.name;
          chip.appendChild(dot);
          chip.appendChild(label);
          chip.addEventListener('click', function () {
            state.colour = colour;
            state.qty = 1;
            render();
          });
          row.appendChild(chip);
        });
        panel.appendChild(row);
        recovery.appendChild(panel);
        recovery.hidden = false;
        return;
      }

      // B — the size is gone in every colour. Offer other styles at about the
      // same EU size, and a WhatsApp message to ask when it is back.
      var target = euOf(state.size);
      var matches = [];
      alternatives.forEach(function (alt) {
        if (matches.length >= 3) return;
        for (var i = 0; i < alt.sizes.length; i++) {
          if (Math.abs(alt.sizes[i].eu - target) <= 1) {
            matches.push({ alt: alt, size: alt.sizes[i] });
            return;
          }
        }
      });

      var head = document.createElement('div');
      head.className = 'ssr-recovery__msg ssr-recovery__msg--lg';
      head.textContent = matches.length ? d.tRecoverySize : d.tRecoveryGone;
      panel.appendChild(head);

      if (matches.length) {
        var sub = document.createElement('div');
        sub.className = 'ssr-recovery__sub';
        sub.textContent = d.tRecoverySizesub;
        panel.appendChild(sub);

        var grid = document.createElement('div');
        grid.className = 'ssr-recovery__alts';
        matches.forEach(function (match) {
          var card = document.createElement('a');
          card.className = 'ssr-recovery__alt';
          card.href = match.alt.url;

          var tile = document.createElement('span');
          tile.className = 'ssr-recovery__altimg';
          if (match.alt.img) {
            var img = document.createElement('img');
            img.src = match.alt.img;
            img.alt = '';
            img.loading = 'lazy';
            img.width = 300;
            img.height = 300;
            tile.appendChild(img);
          }

          var title = document.createElement('span');
          title.className = 'ssr-recovery__alttitle';
          title.textContent = match.alt.title;

          var note = document.createElement('span');
          note.className = 'ssr-recovery__altsize';
          note.textContent = String(t.sizeAvailable || '').replace('[n]', match.size.label);

          card.appendChild(tile);
          card.appendChild(title);
          card.appendChild(note);
          grid.appendChild(card);
        });
        panel.appendChild(grid);
      }

      var notify = document.createElement('a');
      notify.className = 'ssr-wa ssr-wa--block';
      notify.href = waHref(t.waBack);
      notify.target = '_blank';
      notify.rel = 'noopener';
      notify.textContent = d.tNotifyLabel;
      panel.appendChild(notify);

      recovery.appendChild(panel);
      recovery.hidden = false;
    }

    function renderPrice(variant) {
      var price;
      var compare;
      var varies = false;
      if (variant && (state.size || !data.hasSize)) {
        price = variant.price;
        compare = variant.compare;
      } else {
        var p = colourPrices(state.colour);
        if (p.min === null && variant) {
          price = variant.price;
          compare = variant.compare;
        } else {
          price = p.min;
          compare = p.compare;
          varies = p.max !== p.min;
        }
      }
      if (price === null || price === undefined) return;
      if (priceEl) priceEl.textContent = money(price);
      if (fromEl) fromEl.hidden = !varies;
      if (compareEl) {
        if (compare > price) {
          compareEl.textContent = money(compare);
          compareEl.hidden = false;
        } else {
          compareEl.hidden = true;
        }
      }
      if (saveEl) {
        if (compare > price) {
          saveEl.textContent = String(saveEl.getAttribute('data-t-save') || '').replace('[n]', money(compare - price));
          saveEl.hidden = false;
        } else {
          saveEl.hidden = true;
        }
      }
      if (stickyMeta) stickyMeta.textContent = (varies ? t.from + ' ' : '') + money(price);
      return { price: price, varies: varies };
    }

    function render(fromGallery) {
      var variant = variantFor(state.size, state.colour);
      var here = state.size || !data.hasSize ? (variant ? variant.q : 0) : colourTotal(state.colour);
      var anywhere = state.size ? sizeTotal(state.size) : 0;

      renderSizes();
      renderSwatches();
      renderRecovery(here, anywhere);
      renderPicked();
      renderSteps(here);
      var shownPrice = renderPrice(variant);

      if (variant && variantInput && (state.size || !data.hasSize)) variantInput.value = variant.id;

      if (!fromGallery && data.hasColour) {
        var s = slideForColour(state.colour);
        if (s >= 0) goTo(s);
      }

      var chosen = variant && (state.size || !data.hasSize) ? variant : null;
      if (sizeLeft) {
        var chosenQ = chosen ? chosen.q : 0;
        if (chosenQ > 0 && chosenQ <= 6) {
          sizeLeft.textContent = ' · ' + fill(t.left, chosenQ);
          sizeLeft.hidden = false;
        } else {
          sizeLeft.hidden = true;
        }
      }

      var maxQty = variant && variant.q > 0 ? Math.min(variant.q, 9) : 1;
      state.qty = Math.max(1, Math.min(state.qty, maxQty));
      if (qtyInput) qtyInput.value = state.qty;
      if (qtyDisplay) qtyDisplay.textContent = digits(state.qty);
      if (qtyMinus) qtyMinus.setAttribute('aria-disabled', state.qty <= 1 ? 'true' : 'false');
      if (qtyPlus) qtyPlus.setAttribute('aria-disabled', !needsSize() && state.qty >= maxQty ? 'true' : 'false');
      var inBag = !!(chosen && addedIds[chosen.id]);

      var choose = needsSize();
      var available = !choose && here > 0;
      // No price on the button: it sits right above it. Once this pick is in
      // the cart the button says so, and a tap adds one more.
      var label = choose ? t.choose : available ? (inBag && d.tAddedMore ? d.tAddedMore : t.add) : t.soldout;
      if (justAdded) label = d.tAddedShort;

      if (addBtn) {
        addBtn.disabled = !choose && !available;
        addBtn.classList.toggle('is-out', !choose && !available);
        addBtn.classList.toggle('is-choose', choose);
        setLabel(addBtn, label);
      }
      if (buyNow) {
        buyNow.classList.toggle('is-hidden', !available && cartItems < 1);
        buyNow.textContent = cartItems > 0 ? d.tGoCheckout || t.viewCart : d.tBuyNow || t.add;
      }
      if (stickyAdd && !busy) {
        if (inBag) {
          stickyAdd.dataset.mode = 'checkout';
          stickyAdd.disabled = false;
          stickyAdd.classList.remove('is-choose');
          setLabel(stickyAdd, d.tCheckoutCash || t.viewCart);
        } else {
          delete stickyAdd.dataset.mode;
          stickyAdd.disabled = !choose && !available;
          stickyAdd.classList.toggle('is-choose', choose);
          setLabel(stickyAdd, choose ? t.chooseShort : available ? t.add : t.soldout);
        }
      }
      if (stickyPick) stickyPick.textContent = pickParts().join(' · ');
      if (data.hasColour) setThumb(stickyThumb, colourImage(), 120);
      if (waSize) waSize.href = waHref(t.waSize);

      // The order promise under the buttons (ss-promise.js) listens: once a
      // size in stock is picked it turns "2–5 business days" into dates, and
      // it keeps the cash total in step with the price and the quantity.
      var pickNow = pickParts();
      var pick = {
        ready: available,
        variant: chosen ? chosen.id : null,
        price: shownPrice ? shownPrice.price : null,
        varies: !!(shownPrice && shownPrice.varies),
        qty: state.qty,
        // The same " (colour · size)" the size question on WhatsApp uses.
        pickText: pickNow.length ? ' (' + pickNow.join(' · ') + ')' : '',
      };
      root.__ssrPick = pick;
      if (typeof window.CustomEvent === 'function') {
        root.dispatchEvent(new CustomEvent('ssr:pick', { detail: pick }));
      }
    }

    // Tapping Add before a size is chosen takes the shopper to the sizes
    // instead of silently doing nothing.
    function promptSize() {
      if (!sizeStep) return;
      sizeStep.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
      replay(sizeStep, 'is-attention');
      if (sizeHint) {
        sizeHint.hidden = false;
        announce(sizeHint.textContent.trim(), true);
      }
      var firstOk = sizeButtons.filter(function (b) {
        return !b.classList.contains('is-out');
      })[0];
      if (firstOk) {
        setTimeout(function () {
          firstOk.focus({ preventScroll: true });
        }, reduceMotion ? 0 : 350);
      }
    }

    sizeButtons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        state.size = btn.dataset.ssrSize;
        state.qty = 1;
        render();
        if (!cartCache) getCart();
        // On long option lists (12 colours, two rows of sizes) the main button
        // can sit below the fold while the sticky bar stays hidden over the
        // sizes — bring Add into view so buying is always one tap away.
        if (addBtn) {
          var r = addBtn.getBoundingClientRect();
          var stickyOn = sticky && sticky.classList.contains('is-visible');
          if (!stickyOn && r.bottom > window.innerHeight) {
            addBtn.scrollIntoView({ block: 'nearest', behavior: reduceMotion ? 'auto' : 'smooth' });
          }
        }
      });
    });

    swatches.forEach(function (btn) {
      btn.addEventListener('click', function () {
        state.colour = btn.dataset.ssrColour;
        state.qty = 1;
        render();
      });
    });

    root.querySelectorAll('[data-ssr-qty]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var step = parseInt(btn.getAttribute('data-ssr-qty'), 10);
        if (step > 0) {
          if (needsSize()) {
            promptSize();
            return;
          }
          var v = variantFor(state.size, state.colour);
          var max = v && v.q > 0 ? Math.min(v.q, 9) : 1;
          if (state.qty >= max) {
            if (v && v.q > 0 && v.q <= 9) toast(fill(t.limit, v.q));
            return;
          }
        }
        state.qty += step;
        render(true);
      });
    });

    function setBusy(on) {
      busy = on;
      [addBtn, stickyAdd, buyNow].forEach(function (b) {
        if (!b) return;
        b.classList.toggle('is-busy', on);
        if (on) b.setAttribute('aria-busy', 'true');
        else b.removeAttribute('aria-busy');
      });
      if (on && t.adding) {
        setLabel(addBtn, t.adding);
        if (stickyAdd) setLabel(stickyAdd, t.adding);
      }
    }

    // The cart as Shopify holds it, read once and reused for a few seconds:
    // an add must count what is already in the cart, because add.js accepts
    // more units than are in stock.
    function getCart() {
      if (cartCache && Date.now() - cartCache.at < 20000) return cartCache.promise;
      var p = fetch(ROOT + 'cart.js', { headers: { Accept: 'application/json' } }).then(function (r) {
        if (!r.ok) throw new Error('cart');
        return r.json();
      });
      cartCache = { at: Date.now(), promise: p };
      p.catch(function () {
        cartCache = null;
      });
      return p;
    }

    function unitsInCart(cart, id) {
      var n = 0;
      ((cart && cart.items) || []).forEach(function (item) {
        if (item.variant_id === id) n += item.quantity;
      });
      return n;
    }

    function goCheckout() {
      window.location.href = '/checkout';
    }

    function userError(message) {
      var err = new Error(message);
      err.user = true;
      return err;
    }

    function addToCart(then) {
      if (busy) return;
      if (needsSize()) {
        promptSize();
        return;
      }
      var variant = variantFor(state.size, state.colour);
      if (!variant || variant.q <= 0) {
        toast(t.soldout);
        return;
      }
      // Already in the cart this visit: "buy now" means go pay, not add again.
      if (then === 'checkout' && addedIds[variant.id]) {
        goCheckout();
        return;
      }
      setBusy(true);
      getCart()
        .then(
          function (cart) {
            return unitsInCart(cart, variant.id);
          },
          function () {
            return 0;
          }
        )
        .then(function (have) {
          var room = variant.q - have;
          if (room <= 0) throw userError(fill(t.limit, variant.q));
          var quantity = Math.min(state.qty, room);
          return fetch(ROOT + 'cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: variant.id, quantity: quantity }),
          }).then(function (response) {
            if (response.ok) {
              // The add worked; a failed count refresh must not undo that.
              cartCache = null;
              cartItems = Math.max(cartItems, 1);
              refreshCartCount()
                .then(function (cart) {
                  cartItems = cart.item_count;
                  render(true);
                  var bad = rewardCodeProblem(cart);
                  if (bad && d.tCodewarn) toast(String(d.tCodewarn).replace('[code]', bad), { label: t.viewCart, href: ROOT + 'cart' });
                })
                .catch(function () {});
              return;
            }
            return response
              .json()
              .catch(function () {
                return {};
              })
              .then(function () {
                // 422: the cart already holds every unit Shopify will sell.
                throw userError(response.status === 422 ? fill(t.limit, variant.q) : t.error);
              });
          });
        })
        .then(function () {
          addedIds[variant.id] = true;
          if (then === 'checkout') {
            goCheckout();
            return;
          }
          setBusy(false);
          // Confirm in place, not in a popup: the button says so, the line
          // under it links to the cart, and the sticky bar turns into
          // "Checkout" while this pick is the one in the cart.
          justAdded = true;
          render(true);
          setTimeout(function () {
            justAdded = false;
            render(true);
          }, 2400);
          if (added) {
            var addedText = added.querySelector('[data-ssr-added-text]');
            if (addedText) addedText.textContent = [t.added].concat(pickParts()).join(' · ');
            added.removeAttribute('role');
            added.hidden = false;
          }
          announce([t.added, d.title].concat(pickParts()).join(', '));
          trace(addBtn);
          trace(stickyAdd);
        })
        .catch(function (err) {
          setBusy(false);
          render(true);
          toast(err && err.user ? err.message : t.error);
        });
    }

    var form = root.querySelector('[data-ssr-form]');
    if (form) {
      form.addEventListener('submit', function (event) {
        event.preventDefault();
        addToCart();
      });
    }
    if (buyNow) {
      buyNow.addEventListener('click', function () {
        if (cartItems > 0) {
          goCheckout();
          return;
        }
        addToCart('checkout');
      });
      // The cart can change on another page or tab: check it on arrival and
      // when the page comes back from the back/forward cache.
      var syncCartItems = function () {
        cartCache = null;
        getCart().then(
          function (cart) {
            cartItems = cart.item_count;
            render(true);
          },
          function () {}
        );
      };
      syncCartItems();
      window.addEventListener('pageshow', function (event) {
        if (event.persisted) syncCartItems();
      });
    }
    if (stickyAdd) {
      stickyAdd.addEventListener('click', function () {
        if (stickyAdd.dataset.mode === 'checkout') {
          goCheckout();
          return;
        }
        addToCart();
      });
    }

    // Sticky bar: shown whenever the main button is off screen — not yet
    // reached or scrolled past — and hidden while the size sheet is open.
    // Driven by scroll position rather than IntersectionObserver so it still
    // behaves where IO callbacks never fire.
    if (sticky && addBtn) {
      var sheetOpen = false;
      var syncSticky = function () {
        var r = addBtn.getBoundingClientRect();
        var vh = window.innerHeight;
        var off = r.bottom < 0 || r.top > vh - 8;
        // Never on top of the size capsules: hide only while the bar would
        // actually overlap them (they sit in the bottom band it occupies).
        if (sizesGroup) {
          var g = sizesGroup.getBoundingClientRect();
          var band = vh - (sticky.offsetHeight || 72);
          if (g.top < vh && g.bottom > band) off = false;
        }
        var show = off && !sheetOpen;
        sticky.classList.toggle('is-visible', show);
        if (show) sticky.removeAttribute('inert');
        else sticky.setAttribute('inert', '');
        document.documentElement.classList.toggle('ssr-sticky-on', show);
      };
      window.addEventListener('scroll', syncSticky, { passive: true });
      window.addEventListener('resize', syncSticky, { passive: true });
      if (sheet) {
        sheet.onToggle(function (isOpen) {
          sheetOpen = isOpen;
          syncSticky();
        });
      }
      syncSticky();
    }

    render(true);

    // The finder recommends against this product's real sizes, so it needs
    // the same stock view the chips use.
    initSizeFinder(sheet, {
      sizes: function () {
        return sizeButtons.map(function (btn) {
          return {
            label: btn.dataset.ssrSize,
            eu: parseInt(btn.dataset.eu, 10) || 0,
            qty: data.hasColour ? qtyFor(btn.dataset.ssrSize, state.colour) : sizeTotal(btn.dataset.ssrSize),
          };
        });
      },
      select: function (label) {
        state.size = label;
        state.qty = 1;
        render();
      },
    });
  }

  /* -------------------------------------------------------------- feeds */

  // Live product lists (best sellers by real sales) rendered by Shopify's
  // Section Rendering API on the collection route, where the sort order is
  // available to Liquid. Skeleton cards hold the space so nothing jumps.
  function initFeeds(scope) {
    (scope || document).querySelectorAll('[data-ssr-feed-src]').forEach(function (box) {
      if (box.dataset.ssrFeedReady === '1') return;
      box.dataset.ssrFeedReady = '1';
      var max = parseInt(box.getAttribute('data-max'), 10) || 4;
      var exclude = (box.getAttribute('data-exclude') || '').split('|').filter(Boolean);
      var section = box.closest('[data-ssr-feed-section]');

      function fallback() {
        var link = box.getAttribute('data-fallback-url');
        if (section && !link) {
          section.hidden = true;
          return;
        }
        box.setAttribute('aria-busy', 'false');
        box.innerHTML = '';
        if (link) {
          var a = document.createElement('a');
          a.className = 'ssr-btn ssr-btn--ghost';
          a.href = link;
          a.textContent = box.getAttribute('data-fallback-label') || '';
          box.appendChild(a);
        }
      }

      fetch(box.getAttribute('data-ssr-feed-src'), { credentials: 'same-origin' })
        .then(function (r) {
          if (!r.ok) throw new Error('feed');
          return r.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var items = Array.prototype.slice.call(doc.querySelectorAll('[data-ssr-feed-item]'));
          var chosen = items
            .filter(function (item) {
              return exclude.indexOf(item.getAttribute('data-handle')) === -1;
            })
            .slice(0, max);
          if (!chosen.length) return fallback();
          box.innerHTML = '';
          chosen.forEach(function (item) {
            var card = item.firstElementChild;
            if (card) box.appendChild(document.importNode(card, true));
          });
          box.setAttribute('aria-busy', 'false');
        })
        .catch(fallback);
    });
  }

  /* ------------------------------------------------------------- strips */

  // Swipe strips (the home hero stage). Native scroll-snap does the moving;
  // this keeps the dots honest and, when a merchant turns it on, advances every
  // 5 s or more with a pause button, stopping for good on the first touch.
  function initStrips() {
    document.querySelectorAll('[data-ssr-strip]').forEach(function (box) {
      if (box.dataset.ssrReady === '1') return;
      box.dataset.ssrReady = '1';
      var track = box.querySelector('[data-ssr-strip-track]');
      var slides = track ? Array.prototype.slice.call(track.querySelectorAll('[data-ssr-strip-slide]')) : [];
      var dots = Array.prototype.slice.call(box.querySelectorAll('[data-ssr-strip-dot]'));
      var pause = box.querySelector('[data-ssr-strip-pause]');
      if (slides.length < 2) return;

      function step() {
        return Math.abs(slides[1].offsetLeft - slides[0].offsetLeft) || track.clientWidth || 1;
      }
      function current() {
        return Math.max(0, Math.min(slides.length - 1, Math.round(Math.abs(track.scrollLeft) / step())));
      }
      function mark() {
        var i = current();
        dots.forEach(function (dot, n) {
          dot.setAttribute('aria-current', n === i ? 'true' : 'false');
        });
      }
      function go(i) {
        var rtl = getComputedStyle(track).direction === 'rtl';
        track.scrollTo({ left: i * step() * (rtl ? -1 : 1), behavior: reduceMotion ? 'auto' : 'smooth' });
      }

      var timer = null;
      track.addEventListener(
        'scroll',
        function () {
          clearTimeout(timer);
          timer = setTimeout(mark, 120);
        },
        { passive: true }
      );
      dots.forEach(function (dot) {
        dot.addEventListener('click', function () {
          go(parseInt(dot.getAttribute('data-ssr-strip-dot'), 10));
        });
      });
      box.querySelectorAll('[data-ssr-strip-step]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          go((current() + parseInt(btn.getAttribute('data-ssr-strip-step'), 10) + slides.length) % slides.length);
        });
      });

      var seconds = parseInt(box.getAttribute('data-autoplay'), 10) || 0;
      if (!seconds || reduceMotion) {
        if (pause) pause.hidden = true;
        return;
      }
      var ms = Math.max(5000, seconds * 1000);
      var auto = null;
      var stopped = false;
      function start() {
        if (!auto && !stopped && !document.hidden) {
          auto = setInterval(function () {
            go((current() + 1) % slides.length);
          }, ms);
        }
      }
      function stop(forGood) {
        clearInterval(auto);
        auto = null;
        if (forGood) stopped = true;
      }
      ['touchstart', 'pointerdown', 'focusin'].forEach(function (type) {
        box.addEventListener(
          type,
          function (event) {
            if (pause && pause.contains(event.target)) return;
            stop(true);
            if (pause) pause.setAttribute('aria-pressed', 'true');
          },
          { passive: true }
        );
      });
      if (pause) {
        pause.addEventListener('click', function () {
          var paused = pause.getAttribute('aria-pressed') === 'true';
          pause.setAttribute('aria-pressed', paused ? 'false' : 'true');
          if (paused) {
            stopped = false;
            start();
          } else {
            stop(true);
          }
        });
      }
      document.addEventListener('visibilitychange', function () {
        if (document.hidden) stop(false);
        else start();
      });
      start();
    });
  }

  /* --------------------------------------------------------------- cart */

  function initCart() {
    var root = document.querySelector('[data-ssr-cart]');
    if (!root) return;

    root.addEventListener('click', function (event) {
      var btn = event.target.closest('[data-ssr-line]');
      if (!btn) return;
      event.preventDefault();
      // "+" at the units in stock says so instead of posting a change that
      // Shopify would refuse.
      if (btn.getAttribute('aria-disabled') === 'true') {
        toast(root.getAttribute('data-t-no-more') || '');
        return;
      }
      if (root.getAttribute('aria-busy') === 'true') return;
      root.setAttribute('aria-busy', 'true');
      var lineNo = btn.getAttribute('data-ssr-line');
      var kind = btn.classList.contains('ssr-line__remove') ? 'remove' : parseInt(btn.getAttribute('data-ssr-to'), 10) > parseInt(btn.closest('.ssr-line').querySelector('.ssr-line__qtyvalue').textContent.replace(/[٠-٩]/g, function (c) { return '٠١٢٣٤٥٦٧٨٩'.indexOf(c); }), 10) ? 'inc' : 'dec';
      var refused = false;
      var noMore = root.getAttribute('data-t-no-more');
      var failMsg = root.getAttribute('data-t-error');

      fetch(ROOT + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          line: parseInt(btn.getAttribute('data-ssr-line'), 10),
          quantity: parseInt(btn.getAttribute('data-ssr-to'), 10),
        }),
      })
        .then(
          function (response) {
            if (!response.ok) refused = response.status === 422 ? 'stock' : 'error';
          },
          function () {
            refused = 'error';
          }
        )
        .then(function () {
          // Whatever happened, redraw from the cart Shopify actually holds.
          return fetch(location.pathname + '?section_id=' + root.getAttribute('data-section-id'));
        })
        .then(function (response) {
          if (!response.ok) throw new Error('section');
          return response.text();
        })
        .then(function (html) {
          var parsed = new DOMParser().parseFromString(html, 'text/html');
          var fresh = parsed.querySelector('[data-ssr-cart]');
          if (fresh) root.replaceWith(fresh);
          refreshCartCount().catch(function () {});
          initCart();
          initCodeWarn();
          initFeeds();
          // Put focus back where the shopper was, and say what changed.
          var now = document.querySelector('[data-ssr-cart]');
          if (!now) return;
          var target = null;
          var lines = now.querySelectorAll('.ssr-line');
          var line = lines[parseInt(lineNo, 10) - 1];
          if (line && kind !== 'remove') target = line.querySelectorAll('.ssr-line__qtybtn')[kind === 'inc' ? 1 : 0];
          if (!target && lines.length) target = (lines[Math.min(parseInt(lineNo, 10) - 1, lines.length - 1)] || lines[0]).querySelector('.ssr-line__title');
          if (!target) target = now.querySelector('.ssr-cart__title');
          if (target) {
            if (!target.hasAttribute('tabindex') && target.tagName === 'H1') target.setAttribute('tabindex', '-1');
            target.focus({ preventScroll: false });
          }
          if (refused) {
            toast(refused === 'stock' ? noMore : failMsg);
            return;
          }
          var total = now.querySelector('.ssr-summary__total');
          var qty = line && kind !== 'remove' ? line.querySelector('.ssr-line__qtyvalue') : null;
          announce([qty ? qty.textContent.trim() : '', total ? total.innerText.replace(/\s+/g, ' ').trim() : ''].filter(Boolean).join(' · '));
        })
        .catch(function () {
          window.location.reload();
        });
    });
  }

  function initMenu() {
    var menu = document.querySelector('[data-ssr-menu]');
    if (!menu) return;
    function close() {
      menu.removeAttribute('open');
    }
    menu.querySelectorAll('[data-ssr-menu-close]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        close();
        menu.querySelector('summary').focus();
      });
    });
    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && menu.hasAttribute('open')) {
        close();
        menu.querySelector('summary').focus();
      }
    });
    document.addEventListener('click', function (event) {
      if (menu.hasAttribute('open') && !menu.querySelector('.ssr-menu__panel').contains(event.target) && !menu.querySelector('summary').contains(event.target)) close();
    });
    var outside = function () {
      return Array.prototype.slice.call(document.querySelectorAll('#MainContent, .ssr-footer-section, .ssr-brand, .ssr-header__tools, .ssr-announce-section'));
    };
    menu.addEventListener('keydown', function (event) {
      if (event.key !== 'Tab' || !menu.hasAttribute('open')) return;
      var items = Array.prototype.slice.call(menu.querySelectorAll('.ssr-menu__panel a, .ssr-menu__panel button'));
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
    menu.addEventListener('toggle', function () {
      var open = menu.hasAttribute('open');
      outside().forEach(function (el) {
        if (open) el.setAttribute('inert', '');
        else el.removeAttribute('inert');
      });
      if (open) {
        var closeBtn = menu.querySelector('[data-ssr-menu-close]');
        if (closeBtn) closeBtn.focus();
      }
      document.documentElement.classList.toggle('ssr-menu-open', menu.hasAttribute('open'));
    });
  }

  function init() {
    initMenu();
    initCollection();
    initProduct(initSizeGuide());
    initCart();
    initCodeWarn();
    initFeeds();
    initStrips();
  }

  // Some mobile browsers only apply :active on touch once a touch listener
  // exists; this is what makes the foam press show on a phone.
  document.addEventListener('touchstart', function () {}, { passive: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
